/** biome-ignore-all lint/a11y/useSemanticElements: false positive */

import {
	faBuilding,
	faChevronDown,
	faCopy,
	faExclamationTriangle,
	faEye,
	faPen,
	faPlus,
	faRotate,
	faServer,
	faSpinner,
	faTimes,
	faTrash,
	faUser,
	faUserShield
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient, publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth.store";
import {
	type IEnvironment,
	useEnvironmentStore
} from "@/store/environments.store";

interface Brand {
	id: string;
	name: string;
}

interface EnvironmentFormProps {
	initialData: {
		name: string;
		origin: string;
	};
	onSubmit: (e: React.FormEvent) => void;
	isSubmitting: boolean;
	onChange?: (data: { name: string; origin: string }) => void;
}

const AddEnvironmentForm: React.FC<EnvironmentFormProps> = ({
	initialData,
	onSubmit,
	isSubmitting,
	onChange
}) => {
	const { formErrors, resetFormErrors, setFormErrors } = useEnvironmentStore();

	const [formData, setFormData] = React.useState(initialData);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const updatedData = {
			...formData,
			[e.target.name]: e.target.value
		};
		setFormData(updatedData);

		if (onChange) {
			onChange(updatedData);
		}
	};

	const validateAndSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		const newErrors = {
			name: "",
			origin: ""
		};

		let isValid = true;

		if (!formData.name.trim()) {
			newErrors.name = "Environment name is required";
			isValid = false;
		}

		if (!formData.origin.trim()) {
			newErrors.origin = "Origin URL is required";
			isValid = false;
		} else if (!/^https?:\/\//.test(formData.origin)) {
			newErrors.origin =
				"Origin must be a valid URL starting with http:// or https://";
			isValid = false;
		}

		// Update form errors in the store
		if (!isValid) {
			setFormErrors(newErrors);
		} else {
			resetFormErrors();
		}

		if (isValid) {
			onSubmit(e);
		}
	};

	return (
		<div className="h-full">
			<form
				className="relative bg-gradient-to-br from-slate-50 to-white flex flex-col h-full max-w-md mx-auto rounded-lg overflow-hidden"
				onSubmit={validateAndSubmit}
			>
				{/* Form Content */}
				<div className="flex-1 px-6 py-6 space-y-6">
					{/* Environment Name Field */}
					<div className="group">
						<label
							htmlFor="name"
							className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
						>
							<div className="w-2 h-2 rounded-full" />
							Environment Name
							<span className="text-red-500 text-base">*</span>
						</label>
						<div className="relative">
							<input
								name="name"
								type="text"
								placeholder="Enter environment name"
								value={formData.name}
								onChange={handleInputChange}
								className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-300 placeholder:text-slate-400 text-slate-700 font-medium ${
									formErrors.name
										? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
										: "border-slate-200"
								}`}
							/>
							{formErrors.name && (
								<p className="mt-2 text-sm text-red-600 font-medium">
									{formErrors.name}
								</p>
							)}
						</div>
					</div>

					{/* Origin URL Field */}
					<div className="group">
						<label
							htmlFor="origin"
							className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
						>
							<div className="w-2 h-2 rounded-full" />
							Origin URL
							<span className="text-red-500 text-base">*</span>
						</label>
						<div className="relative">
							<input
								name="origin"
								type="text"
								placeholder="https://example.com"
								value={formData.origin}
								onChange={handleInputChange}
								className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-300 placeholder:text-slate-400 text-slate-700 font-medium ${
									formErrors.origin
										? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
										: "border-slate-200"
								}`}
							/>
							{formErrors.origin && (
								<p className="mt-2 text-sm text-red-600 font-medium">
									{formErrors.origin}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Footer with Action Button */}
				<div className="px-6 py-6 bg-gradient-to-t from-slate-50 to-transparent border-t border-slate-100">
					<button
						type="submit"
						disabled={isSubmitting}
						className="w-full bg-blue-fynxt text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex justify-center items-center"
					>
						<div className="relative flex items-center justify-center gap-3">
							{isSubmitting ? (
								<div className="flex items-center gap-2">
									<FontAwesomeIcon icon={faSpinner} spin className="text-lg" />
									<span>Creating Environment...</span>
								</div>
							) : (
								<span>Create Environment</span>
							)}
						</div>
					</button>
				</div>

				{/* Decorative Elements */}
				<div className="absolute top-4 right-4 w-1 h-8 bg-gradient-to-b from-blue-400 to-transparent rounded-full opacity-30" />
				<div className="absolute bottom-4 left-4 w-1 h-6 bg-gradient-to-t from-emerald-400 to-transparent rounded-full opacity-30" />
			</form>
		</div>
	);
};

const Header = () => {
	const {
		resetAuth,
		brandDetails,
		scope,
		isSuperAdmin,
		isBrand,
		brandId,
		userId,
		userName,
		brandName,
		setAuth,
		setUser,
		setLogin
	} = useAuthStore();
	const location = useLocation();
	const navigate = useNavigate();
	const [brands, setBrands] = useState<Brand[]>([]);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const brandSwitcherRef = useRef<HTMLDivElement>(null);
	const brandEnvironmentsRef = useRef<HTMLDivElement>(null);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [brandSwitcherOpen, setBrandSwitcherOpen] = useState(false);
	const [brandEnvironmentsOpen, setBrandEnvironmentsOpen] = useState(false);

	const {
		environments,
		selectedEnvironmentId,
		environmentToRotate,
		isRotateModalOpen,
		environmentToDelete,
		isDeleteModalOpen,
		formData,
		isLoading,
		showTokenCopied,
		showSecretCopied,
		setEnvironmentToRotate,
		setIsRotateModalOpen,
		setEnvironmentToDelete,
		setIsDeleteModalOpen,
		setEnvironments,
		setSelectedEnvironmentId,
		setSelectedEnvironmentSecret,
		deleteEnvironment,
		resetFormErrors,
		updateEnvironment,
		setFormData,
		resetFormData,
		setLoading,
		addEnvironment,
		setShowTokenCopied,
		setShowSecretCopied
	} = useEnvironmentStore();

	const [isCreateEnvModalOpen, setIsCreateEnvModalOpen] = useState(false);
	const [isViewEnvModalOpen, setIsViewEnvModalOpen] = useState(false);
	const [viewEnv, setViewEnv] = useState<IEnvironment | null>(null);
	const [isRotatingSecret, setIsRotatingSecret] = useState(false);
	const [isDeletingEnv, setIsDeletingEnv] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editEnvForm, setEditEnvForm] = useState({ name: "", origin: "" });
	const [isUpdatingEnv, setIsUpdatingEnv] = useState(false);

	const selectedEnvironmentName = environments.find(
		(env) => env.id === selectedEnvironmentId
	)?.name;

	useEffect(() => {
		const fetchBrands = async () => {
			try {
				if (brandId && userId && isBrand === "true") {
					const { data } = await apiClient.get<Brand[]>(
						resolveEndpoint(
							API_ENDPOINTS,
							["BRAND", "USERS", "GET_BY_USER_ID"],
							brandId,
							userId
						)
					);
					setBrands(data);
				}
			} catch (_error) {
				toast.error("Failed to fetch brands. Please try again.");
			}
		};

		fetchBrands();
	}, [brandId, userId, isBrand]);

	useEffect(() => {
		const fetchBrandsEnv = async () => {
			try {
				if (brandId && isBrand === "true") {
					setLoading(true);
					const { data } = await apiClient.get<IEnvironment[]>(
						resolveEndpoint(API_ENDPOINTS, ["ENVIRONMENTS", "LIST"]),
						{
							headers: {
								"X-Brand-Id": brandId
							}
						}
					);
					setEnvironments(data);
				}
			} catch (_error) {
				toast.error("Failed to fetch environments. Please try again.");
			} finally {
				setLoading(false);
			}
		};

		fetchBrandsEnv();
	}, [brandId, isBrand, setEnvironments, setLoading]);

	useEffect(() => {
		if (isBrand !== "true") return;

		if (environments.length === 0) {
			if (selectedEnvironmentId !== null) {
				setSelectedEnvironmentId(null);
				setSelectedEnvironmentSecret(null);
			}
			return;
		}

		if (
			!selectedEnvironmentId ||
			!environments.some((e) => e.id === selectedEnvironmentId)
		) {
			setSelectedEnvironmentId(environments[0].id);
			setSelectedEnvironmentSecret(environments[0].secret);
		}
	}, [
		environments,
		isBrand,
		selectedEnvironmentId,
		setSelectedEnvironmentId,
		setSelectedEnvironmentSecret
	]);

	const brandList: Brand[] =
		scope === "SYSTEM" ? brands : JSON.parse(brandDetails || "[]");

	const handleClickOutside = useCallback((e: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(e.target as Node)
		) {
			setDropdownOpen(false);
		}
		if (
			brandSwitcherRef.current &&
			!brandSwitcherRef.current.contains(e.target as Node)
		) {
			setBrandSwitcherOpen(false);
		}
		if (
			brandEnvironmentsRef.current &&
			!brandEnvironmentsRef.current.contains(e.target as Node)
		) {
			setBrandEnvironmentsOpen(false);
		}
	}, []);

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [handleClickOutside]);

	const handleBrandSwitch = async (
		isAdmin: boolean,
		brandId = "",
		brandName = ""
	) => {
		try {
			const { brandId: currentBrandId, refreshToken } = useAuthStore.getState();
			if (!isAdmin && currentBrandId === brandId) {
				return;
			}

			const tokenRes = await publicApiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["AUTH", "REFRESH"]),
				{
					refreshToken,
					...(isAdmin ? {} : { brandId })
				}
			);

			const { accessToken, expiresAt, userId, userName } = tokenRes.data;

			if (!accessToken || !refreshToken || !expiresAt) {
				throw new Error("Invalid authentication response");
			}

			setAuth({
				accessToken,
				refreshToken,
				expiresAt: expiresAt.toString()
			});
			setUser(userId, userName);
			setLogin({
				isBrand: (!isAdmin).toString(),
				isSuperAdmin: isAdmin.toString(),
				brandId: isAdmin ? "" : brandId,
				brandName: isAdmin ? "" : brandName
			});

			setBrandSwitcherOpen(false);

			toast.success(
				`Switched to ${isAdmin ? "Super Admin" : `Brand: ${brandName}`}`
			);

			navigate(isAdmin ? "/admin/brands" : `/${brandId}`, { replace: true });
		} catch (error) {
			toast.error("Failed to switch brand. Please try again.");
			console.error("Brand switch error:", error);
		}
	};

	const getPageTitle = (pathname: string) => {
		// Extract meaningful segments from the URL path
		const segments = pathname.toLowerCase().split("/").filter(Boolean);

		// Skip admin/brandId segments and get the last meaningful segment
		let pageSegment = "";
		if (segments.length > 0) {
			// If first segment is 'admin' or looks like a brandId, take the next segment
			if (segments[0] === "admin" || segments[0].includes("_")) {
				pageSegment = segments.length > 1 ? segments[1] : "";
			} else {
				// Otherwise take the last segment
				pageSegment = segments[segments.length - 1];
			}
		}

		// If we have a page segment, capitalize it for display
		if (pageSegment) {
			// Handle special cases or formatting if needed
			return pageSegment.charAt(0).toUpperCase() + pageSegment.slice(1);
		}

		return "";
	};

	const handleLogout = useCallback(async () => {
		try {
			const refreshToken = useAuthStore.getState().refreshToken;

			await apiClient.post(resolveEndpoint(API_ENDPOINTS, ["AUTH", "LOGOUT"]), {
				refreshToken
			});

			resetAuth();
		} catch (_error) {
			resetAuth();
		}
	}, [resetAuth]);

	const truncateUsername = (name: string | null): string => {
		if (!name) return "User";
		return name.length > 6 ? `${name.substring(0, 6)}...` : name;
	};

	const handleCreateEnvironment = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!formData.name || !formData.origin) return;
		try {
			setLoading(true);
			const { data: created } = await apiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["ENVIRONMENTS", "CREATE"]),
				formData,
				{
					headers: { "X-Brand-Id": brandId }
				}
			);
			addEnvironment(created);
			setSelectedEnvironmentId(created.id);
			setSelectedEnvironmentSecret(created.secret);
			toast.success("Environment created");
			closeCreateEnvModal();
			resetFormData();
		} catch (_error) {
			toast.error("Failed to create environment");
		} finally {
			setLoading(false);
		}
	};

	const closeCreateEnvModal = useCallback(() => {
		setIsCreateEnvModalOpen(false);
		resetFormData();
		resetFormErrors();
	}, [resetFormData, resetFormErrors]);

	const openEnvDetails = async (id: string) => {
		try {
			const { data } = await apiClient.get<IEnvironment>(
				resolveEndpoint(API_ENDPOINTS, ["ENVIRONMENTS", "GET_BY_ID"], id),
				{ headers: { "X-Brand-Id": brandId } }
			);
			setViewEnv(data);
			setIsViewEnvModalOpen(true);
		} catch (_error) {
			toast.error("Failed to load environment details");
		}
	};

	// Copy helper - using Zustand store for copy state
	const handleCopy = (value: string, field: "token" | "secret") => {
		navigator.clipboard.writeText(value).then(() => {
			if (field === "token") {
				setShowTokenCopied(viewEnv?.id || null);
				setTimeout(() => setShowTokenCopied(null), 1500);
			} else {
				setShowSecretCopied(viewEnv?.id || null);
				setTimeout(() => setShowSecretCopied(null), 1500);
			}
		});
	};

	// Rotate secret
	const handleRotateSecret = async (id: string) => {
		try {
			setIsRotatingSecret(true);
			const { data } = await apiClient.put<IEnvironment>(
				resolveEndpoint(API_ENDPOINTS, ["ENVIRONMENTS", "ROTATE_SECRET"], id),
				{},
				{ headers: { "X-Brand-Id": brandId } }
			);
			updateEnvironment(data);
			setViewEnv(data);
			toast.success("Secret rotated");
		} catch (_error) {
			toast.error("Failed to rotate secret");
		} finally {
			setIsRotatingSecret(false);
		}
	};

	// Delete environment handler
	const handleDeleteEnvironment = async (id: string) => {
		setIsDeletingEnv(true);
		try {
			await apiClient.delete(
				resolveEndpoint(API_ENDPOINTS, ["ENVIRONMENTS", "DELETE_BY_ID"], id),
				{ headers: { "X-Brand-Id": brandId } }
			);
			deleteEnvironment(id);
			toast.success("Environment deleted");
			setIsDeleteModalOpen(false);
			setEnvironmentToDelete(null);
			setIsViewEnvModalOpen(false);
			setIsEditMode(false);
			if (selectedEnvironmentId === id) {
				setSelectedEnvironmentId(null);
				setSelectedEnvironmentSecret(null);
			}
		} catch (_error) {
			toast.error("Failed to delete environment");
		} finally {
			setIsDeletingEnv(false);
		}
	};

	// Update environment handler
	const handleUpdateEnvironment = async (id: string) => {
		setIsUpdatingEnv(true);
		try {
			const { data: updated } = await apiClient.put<IEnvironment>(
				resolveEndpoint(API_ENDPOINTS, ["ENVIRONMENTS", "UPDATE_BY_ID"], id),
				editEnvForm,
				{ headers: { "X-Brand-Id": brandId } }
			);
			updateEnvironment(updated);
			setViewEnv(updated);
			toast.success("Environment updated");
			setIsEditMode(false);
		} catch (_error) {
			toast.error("Failed to update environment");
		} finally {
			setIsUpdatingEnv(false);
		}
	};

	// Function to mask sensitive data, showing only the last 4 characters out of 8 total
	const maskSensitiveData = (text: string | undefined) => {
		if (!text) return "";
		if (text.length <= 4) return text;

		// Ensure we only display 8 characters total
		const displayText = text.length > 8 ? text.slice(-8) : text;
		const visiblePart = displayText.slice(-4);
		const maskedPart = displayText.slice(0, -4).replace(/./g, "*");

		return maskedPart + visiblePart;
	};

	// Function to truncate origin URL to show only first 25 characters
	const truncateOrigin = (url: string | undefined) => {
		if (!url) return "";
		if (url.length <= 25) return url;

		return `${url.substring(0, 25)}....`;
	};

	return (
		<header className="bg-white px-6 py-2 shadow flex justify-between items-center md:ml-0">
			<h1 className="text-xl font-semibold">
				{getPageTitle(location.pathname)}
			</h1>
			<div className="flex items-center gap-4">
				<div className="relative" ref={brandSwitcherRef}>
					{isSuperAdmin !== "true" ? (
						<button
							type="button"
							onClick={
								isSuperAdmin === "true"
									? undefined
									: () => setBrandSwitcherOpen(!brandSwitcherOpen)
							}
							className={`w-auto flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-100 rounded-md border-gray-300 ${
								isSuperAdmin === "true" ? "cursor-default" : "cursor-pointer"
							}`}
						>
							{scope === "SYSTEM" && isSuperAdmin === "true" ? (
								<FontAwesomeIcon
									icon={faUserShield}
									className={`text-[14px] ${
										isSuperAdmin === "true" ? "text-red-700" : "text-[#0099FF]"
									}`}
								/>
							) : (
								<FontAwesomeIcon
									icon={faBuilding}
									className="text-[14px] text-[#0099FF]"
								/>
							)}

							<span>
								{scope === "SYSTEM" && isSuperAdmin === "true"
									? "Super Admin"
									: brandList.find((b) => b.id === brandId)?.name || brandName}
							</span>
							<FontAwesomeIcon
								icon={faChevronDown}
								className="text-[14px] text-gray-500"
							/>
						</button>
					) : (
						<div className="w-auto flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md border-gray-300 hidden">
							<FontAwesomeIcon
								icon={faUserShield}
								className="text-[14px] text-red-700"
							/>
							<span>Super Admin</span>
						</div>
					)}

					{brandSwitcherOpen && isSuperAdmin !== "true" && (
						<div
							className="absolute right-0 mt-2 w-56 bg-white shadow-lg z-[1000] border rounded-md flex flex-col"
							style={{ maxHeight: "80vh", overflow: "visible" }}
						>
							{/* Super Admin Option */}
							{isBrand === "true" && scope === "SYSTEM" && (
								<div className="border-b">
									<button
										type="button"
										className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
										onClick={() => handleBrandSwitch(true)}
									>
										<FontAwesomeIcon
											icon={faUserShield}
											className="text-[14px] text-red-700"
										/>
										<span className="font-medium">Super Admin</span>
									</button>
								</div>
							)}

							{/* All brands in a scrollable list */}
							<div
								className={brandList.length > 4 ? "overflow-y-auto" : ""}
								style={{
									maxHeight: "135px"
								}}
							>
								{brandList.map((brand) => (
									<button
										key={brand.id}
										type="button"
										className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
											isBrand === "true" && brandId === brand.id
												? "bg-blue-50 text-blue-600"
												: ""
										}`}
										onClick={() =>
											handleBrandSwitch(false, brand.id, brand.name)
										}
									>
										<FontAwesomeIcon
											icon={faBuilding}
											className={`text-[14px] ${
												isBrand === "true" && brandId === brand.id
													? "text-blue-600"
													: "text-gray-500"
											}`}
										/>
										<span>{brand.name}</span>
									</button>
								))}
							</div>
						</div>
					)}
				</div>

				{isBrand === "true" && (
					<div className="relative mr-2" ref={brandEnvironmentsRef}>
						{selectedEnvironmentName && environments.length > 0 ? (
							<button
								type="button"
								onClick={() => setBrandEnvironmentsOpen(!brandEnvironmentsOpen)}
								className="w-auto flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md border-gray-300 cursor-pointer"
							>
								<FontAwesomeIcon
									icon={faServer}
									className="text-[14px] text-[#0099FF]"
								/>
								<span>{selectedEnvironmentName}</span>
								<span
									role="button"
									tabIndex={0}
									onClick={(e) => {
										e.stopPropagation();
										if (selectedEnvironmentId)
											openEnvDetails(selectedEnvironmentId);
									}}
									onKeyDown={(e) => {
										if (
											(e.key === "Enter" || e.key === " ") &&
											selectedEnvironmentId
										) {
											e.preventDefault();
											e.stopPropagation();
											openEnvDetails(selectedEnvironmentId);
										}
									}}
									className="text-blue-600 hover:text-blue-900 p-1 cursor-pointer focus:outline-none"
									title="View environment details"
								>
									<FontAwesomeIcon icon={faEye} />
								</span>
								<FontAwesomeIcon
									icon={faChevronDown}
									className="text-[14px] text-gray-500"
								/>
							</button>
						) : (
							<button
								type="button"
								className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm hover:bg-gray-100"
								onClick={() => {
									setBrandEnvironmentsOpen(false);
									setIsCreateEnvModalOpen(true);
								}}
							>
								<FontAwesomeIcon icon={faPlus} className="text-[12px]" />
								<span>Create Env</span>
							</button>
						)}

						{brandEnvironmentsOpen && (
							<div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border max-h-60 overflow-y-auto">
								<div className="py-1">
									{environments.length &&
										environments.map((env) => (
											<button
												type="button"
												key={env.id}
												className={`w-full gap-2 text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
													env.id === selectedEnvironmentId
														? "bg-blue-50 text-blue-600"
														: ""
												}`}
												onClick={() => {
													setSelectedEnvironmentId(env.id);
													setSelectedEnvironmentSecret(env.secret);
													setBrandEnvironmentsOpen(false);
												}}
											>
												<FontAwesomeIcon
													icon={faServer}
													className={`text-[14px] ${
														selectedEnvironmentId === env.id
															? "text-blue-600"
															: "text-gray-500"
													}`}
												/>
												<span>{env.name}</span>
											</button>
										))}
									{/* divider */}
									<div className="border-t my-1" />

									{/* Create environment */}
									<button
										type="button"
										className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm hover:bg-gray-100"
										onClick={() => {
											setBrandEnvironmentsOpen(false);
											setIsCreateEnvModalOpen(true);
										}}
									>
										<FontAwesomeIcon icon={faPlus} className="text-[12px]" />
										<span>Create Env</span>
									</button>
								</div>
							</div>
						)}

						{isCreateEnvModalOpen && (
							<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
								<div className="w-full max-w-md bg-white rounded-xl shadow-xl transform transition-all duration-300">
									{/* Modal Header */}
									<div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-xl">
										<h2 className="text-lg font-semibold text-gray-800">
											Create Environment
										</h2>
										<button
											type="button"
											className="text-gray-400 hover:text-gray-600"
											onClick={closeCreateEnvModal}
										>
											<FontAwesomeIcon icon={faTimes} />
										</button>
									</div>

									{/* Modal Body */}
									<div className="p-6">
										<AddEnvironmentForm
											initialData={formData}
											isSubmitting={isLoading}
											onSubmit={handleCreateEnvironment}
											onChange={(d: { name: string; origin: string }) =>
												setFormData(d)
											}
										/>
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				{isViewEnvModalOpen && viewEnv && (
					<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
						<div className="w-full sm:w-[520px] bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-xl transform transition-all duration-300 overflow-hidden max-h-[90vh]">
							{/* Header */}
							<div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
								<h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
									<div className="w-2 h-2 rounded-full" />
									{isEditMode ? "Edit Environment" : "Environment Details"}
								</h2>
								<button
									type="button"
									className="text-slate-400 hover:text-slate-600 transition-colors"
									onClick={() => {
										setIsViewEnvModalOpen(false);
										setViewEnv(null);
										setIsEditMode(false);
									}}
								>
									<FontAwesomeIcon icon={faTimes} />
								</button>
							</div>

							{/* Body - Scrollable Content */}
							<div className="overflow-y-auto flex-1">
								{isEditMode ? (
									<div className="p-6 space-y-6">
										{/* Environment Name Field */}
										<div className="group">
											<label
												htmlFor="edit-env-name"
												className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
											>
												<div className="w-2 h-2 rounded-full" />
												Environment Name
												<span className="text-red-500 text-base">*</span>
											</label>
											<div className="relative">
												<input
													type="text"
													value={editEnvForm.name}
													onChange={(e) =>
														setEditEnvForm({
															...editEnvForm,
															name: e.target.value
														})
													}
													className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-xl shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-300 placeholder:text-slate-400 text-slate-700 font-medium"
													placeholder="Enter environment name"
												/>
											</div>
										</div>

										{/* Origin Field */}
										<div className="group">
											<label
												htmlFor="edit-env-origin"
												className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
											>
												<div className="w-2 h-2 rounded-full" />
												Origin URL
												<span className="text-red-500 text-base">*</span>
											</label>
											<div className="relative">
												<input
													type="text"
													value={editEnvForm.origin}
													onChange={(e) =>
														setEditEnvForm({
															...editEnvForm,
															origin: e.target.value
														})
													}
													className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-xl shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-300 placeholder:text-slate-400 text-slate-700 font-medium"
													placeholder="https://example.com"
												/>
											</div>
										</div>

										{/* Decorative Elements */}
										<div className="absolute top-4 right-4 w-1 h-8 bg-gradient-to-b from-blue-400 to-transparent rounded-full opacity-30" />
										<div className="absolute bottom-4 left-4 w-1 h-6 bg-gradient-to-t from-emerald-400 to-transparent rounded-full opacity-30" />
									</div>
								) : (
									<div className="p-6">
										<div className="space-y-6">
											{/* Name Field */}
											<div className="group">
												<label
													htmlFor="name"
													className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
												>
													<div className="w-2 h-2 rounded-full" />
													Environment Name
												</label>
												<div className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-xl shadow-sm text-slate-700 font-medium">
													{viewEnv.name}
												</div>
											</div>

											{/* Token Field */}
											<div className="group">
												<label
													htmlFor="token"
													className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
												>
													<div className="w-2 h-2 rounded-full" />
													Token
												</label>
												<div className="flex items-center gap-3">
													<div className="flex-1 px-4 py-3.5 bg-white border-2 border-slate-200 rounded-xl shadow-sm font-mono text-slate-700 select-all break-all">
														{maskSensitiveData(viewEnv.token)}
													</div>
													<button
														type="button"
														className="flex items-center justify-center w-10 h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors shadow-sm"
														title="Copy token"
														onClick={() => handleCopy(viewEnv.token, "token")}
													>
														<FontAwesomeIcon icon={faCopy} />
													</button>
												</div>
												{showTokenCopied === viewEnv.id && (
													<p className="mt-2 text-sm text-green-600 font-medium">
														Token copied!
													</p>
												)}
											</div>

											{/* Secret Field */}
											<div className="group">
												<label
													htmlFor="secret"
													className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
												>
													<div className="w-2 h-2 rounded-full" />
													Secret
												</label>
												<div className="flex items-center gap-3">
													<div className="flex-1 px-4 py-3.5 bg-white border-2 border-slate-200 rounded-xl shadow-sm font-mono text-slate-700 select-all break-all">
														{maskSensitiveData(viewEnv.secret)}
													</div>
													<button
														type="button"
														className="flex items-center justify-center w-10 h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors shadow-sm"
														title="Copy secret"
														onClick={() => handleCopy(viewEnv.secret, "secret")}
													>
														<FontAwesomeIcon icon={faCopy} />
													</button>
													<button
														type="button"
														className="flex items-center justify-center w-10 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm"
														title="Rotate secret"
														onClick={() => {
															setEnvironmentToRotate(viewEnv);
															setIsRotateModalOpen(true);
														}}
														disabled={isRotatingSecret}
													>
														{isRotatingSecret ? (
															<FontAwesomeIcon icon={faSpinner} spin />
														) : (
															<FontAwesomeIcon icon={faRotate} />
														)}
													</button>
												</div>
												{showSecretCopied === viewEnv.id && (
													<p className="mt-2 text-sm text-green-600 font-medium">
														Secret copied!
													</p>
												)}
											</div>

											{/* Origin Field */}
											<div className="group">
												<label
													htmlFor="origin"
													className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"
												>
													<div className="w-2 h-2 rounded-full" />
													Origin URL
												</label>
												<div className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-xl shadow-sm text-slate-700 font-medium break-all">
													{truncateOrigin(viewEnv.origin)}
												</div>
											</div>
										</div>

										{/* Decorative Elements */}
										<div className="absolute top-4 right-4 w-1 h-8 bg-gradient-to-b from-blue-400 to-transparent rounded-full opacity-30" />
										<div className="absolute bottom-4 left-4 w-1 h-6 bg-gradient-to-t from-emerald-400 to-transparent rounded-full opacity-30" />
									</div>
								)}
							</div>

							{/* Footer Actions */}
							{isEditMode ? (
								<div className="px-6 py-6 bg-gradient-to-t from-slate-50 to-transparent border-t border-slate-100 flex justify-end gap-3">
									<button
										type="button"
										className="px-6 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 shadow-sm"
										onClick={() => setIsEditMode(false)}
									>
										Cancel
									</button>
									<button
										type="button"
										className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60 transition-all duration-200 shadow-sm"
										onClick={() => handleUpdateEnvironment(viewEnv.id)}
										disabled={isUpdatingEnv}
									>
										{isUpdatingEnv ? (
											<>
												<FontAwesomeIcon icon={faSpinner} spin />
												Saving...
											</>
										) : (
											<>
												<FontAwesomeIcon icon={faPen} />
												Save Changes
											</>
										)}
									</button>
								</div>
							) : (
								<div className="px-6 py-6 bg-gradient-to-t from-slate-50 to-transparent border-t border-slate-100 flex justify-end gap-3">
									<button
										type="button"
										className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm"
										onClick={() => {
											setEditEnvForm({
												name: viewEnv.name,
												origin: viewEnv.origin
											});
											setIsEditMode(true);
										}}
									>
										<FontAwesomeIcon icon={faPen} />
										Edit Environment
									</button>
									<button
										type="button"
										className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm"
										onClick={() => {
											setEnvironmentToDelete(viewEnv);
											setIsDeleteModalOpen(true);
										}}
									>
										<FontAwesomeIcon icon={faTrash} />
										Delete Environment
									</button>
								</div>
							)}
						</div>
					</div>
				)}

				{isRotateModalOpen && environmentToRotate && (
					<div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
						<div className="w-full max-w-md bg-white rounded-lg shadow-xl">
							<div className="px-6 py-4 border-b flex items-center gap-3">
								<div className="h-10 w-10 flex items-center justify-center bg-yellow-100 rounded-full">
									<FontAwesomeIcon
										icon={faExclamationTriangle}
										className="text-yellow-600"
									/>
								</div>
								<h3 className="text-lg font-semibold text-gray-800">
									Rotate Secret
								</h3>
							</div>
							<div className="p-6 text-sm text-gray-700">
								Are you sure you want to rotate the secret for{" "}
								<span className="font-medium">{environmentToRotate.name}</span>?
								This action cannot be undone.
							</div>
							<div className="px-6 py-3 bg-gray-50 flex justify-end gap-3">
								<button
									type="button"
									className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-60"
									onClick={() => {
										handleRotateSecret(environmentToRotate.id);
										setIsRotateModalOpen(false);
										setEnvironmentToRotate(null);
									}}
									disabled={isRotatingSecret}
								>
									{isRotatingSecret ? (
										<>
											<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />{" "}
											Rotating...
										</>
									) : (
										"Rotate"
									)}
								</button>
								<button
									type="button"
									className="bg-white border text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-100"
									onClick={() => {
										setIsRotateModalOpen(false);
										setEnvironmentToRotate(null);
									}}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				)}

				{isDeleteModalOpen && environmentToDelete && (
					<div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
						<div className="w-full max-w-md bg-white rounded-lg shadow-xl">
							<div className="px-6 py-4 border-b flex items-center gap-3">
								<div className="h-10 w-10 flex items-center justify-center bg-red-100 rounded-full">
									<FontAwesomeIcon
										icon={faExclamationTriangle}
										className="text-red-600"
									/>
								</div>
								<h3 className="text-lg font-semibold text-gray-800">
									Delete Environment
								</h3>
							</div>
							<div className="p-6 text-sm text-gray-700">
								Are you sure you want to delete{" "}
								<span className="font-medium">{environmentToDelete.name}</span>?
								This action cannot be undone.
							</div>
							<div className="px-6 py-3 bg-gray-50 flex justify-end gap-3">
								<button
									type="button"
									className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-60"
									onClick={() =>
										handleDeleteEnvironment(environmentToDelete.id)
									}
									disabled={isDeletingEnv}
								>
									{isDeletingEnv ? (
										<>
											<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />{" "}
											Deleting...
										</>
									) : (
										"Delete"
									)}
								</button>
								<button
									type="button"
									className="bg-white border text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-100"
									onClick={() => {
										setIsDeleteModalOpen(false);
										setEnvironmentToDelete(null);
									}}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				)}

				<div className="text-sm">
					<div className="relative" ref={dropdownRef}>
						<button
							type="button"
							onClick={() => setDropdownOpen(!dropdownOpen)}
							className="focus:outline-none text-[#0099ff] border-gray-300 hover:text-gray-800 bg-gray-100 rounded-full px-[8px] py-1 w-[35px] h-[35px] leading-[28px]"
						>
							<FontAwesomeIcon
								icon={faUser}
								className={"text-[14px] text-blue-fynxt_1"}
							/>
						</button>

						{dropdownOpen && (
							<div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border profile_box">
								<div
									className={
										"bg-[#002666] border-b px-4 py-3 text-gray-700 text-left text-sm text-white"
									}
								>
									Welcome, {truncateUsername(userName)}
								</div>
								<button
									type="button"
									className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 rounded-none"
									onClick={handleLogout}
								>
									Logout
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</header>
	);
};

export default Header;
